/*!
 * Copyright (C) Octillion Media LLC 2019
 * All Rights Reserved.
 */
export const namespaced = true;
import { getDashboardStats, downloadDashboards, markAllReadDownload } from '../../helpers/api';
import { toastMsg } from '@/helpers/util';
import EventBus from '@/event-bus.js';
/**
 * Vuex module for managing device statistics in the dashboard.
 * This module fetches and stores device performance statistics.
 */
export const state = {
  devicesStats: [],
  dashboardAnalytics: [],
  performanceByCampaign: [],
  performanceByCreatives: [],
  performanceByEvents: [],
  performanceByGeo: [],
  performanceByProviders: [],
  performanceBySports: [],
  downloadDashboardStats: [],
  downloadDashboardStatsError: null,
  loader: {
    devicesStats: false,
    dashboardAnalytics: false,
    performanceByCampaign: false,
    performanceByCreatives: false,
    performanceByEvents: false,
    performanceByGeo: false,
    performanceByProviders: false,
    performanceBySports: false,
  },
  downloadLoader: {
    devicesStats: false,
    dashboardAnalytics: false,
    performanceByCampaign: false,
    performanceByCreatives: false,
    performanceByEvents: false,
    performanceByGeo: false,
    performanceByProviders: false,
    performanceBySports: false,
  }
}

export const getters = {
  devicesStats: state => state.devicesStats,
  dashboardAnalytics: state => state.dashboardAnalytics,
  performanceByCampaign: state => state.performanceByCampaign,
  performanceByCreatives: state => state.performanceByCreatives,
  performanceByEvents: state => state.performanceByEvents,
  performanceByGeo: state => state.performanceByGeo,
  performanceByProviders: state => state.performanceByProviders,
  performanceBySports: state => state.performanceBySports,
  loader: state => state.loader,
  downloadLoader: state => state.downloadLoader,
  downloadDashboardStats: state => state.downloadDashboardStats,
  downloadDashboardStatsError: state => state.downloadDashboardStatsError
};

export const mutations = {
  setDeviceStats(state, payload) {
    state.devicesStats = payload;
  },
  setDashboardAnalytics(state, payload) {
    state.dashboardAnalytics = payload;
  },
  setPerformanceByCampaign(state, payload) {
    state.performanceByCampaign = payload;
  },
  setPerformanceByCreatives(state, payload) {
    state.performanceByCreatives = payload;
  },
  setPerformanceByEvents(state, payload) {
    state.performanceByEvents = payload;
  },
  setPerformanceByGeo(state, payload) {
    state.performanceByGeo = payload;
  },
  setPerformanceByProviders(state, payload) {
    state.performanceByProviders = payload;
  },
  setPerformanceBySports(state, payload) {
    state.performanceBySports = payload;
  },
  setLoader(state, { key, value, isDownload = false }) {
    if (Object.prototype.hasOwnProperty.call(state.loader, key)) {
      state.loader[key] = value;
    }
  },
  setDownloadLoader(state, { key, value }) {
    if (Object.prototype.hasOwnProperty.call(state.downloadLoader, key)) {
      state.downloadLoader[key] = value;
    }
  },
  setDownloadDashboardsSuccess(state, newValue) {
    state.downloadDashboardStats = newValue
  },
  setDownloadDashboardsErr(state, newValue) {
    state.downloadDashboardStatsError = newValue
  },
}

export const actions = {
  async getPerformanceByDevice({ commit }, payload) {
    const isExport = !!payload.export;
    if (!isExport) {
      commit('setLoader', { key: 'devicesStats', value: true });
    }
    if(isExport) {
      commit('setDownloadLoader', { key: 'devicesStats', value: true });
    }
    try {
      const response = await getDashboardStats('devices-stats', payload);
      if (isExport && response?.data?.job_data_id) {
        toastMsg(`Device download request raised successfully with Job ID (${response?.data?.job_data_id})`, 'success');
        EventBus.emit('reloadDownload');
      } else {
        commit('setDeviceStats', response.data.devices_list);
      }
    } catch (error) {
      commit('setDeviceStats', []);
    } finally {
      commit('setLoader', { key: 'devicesStats', value: false });
      commit('setDownloadLoader', { key: 'devicesStats', value: false });
    }
  },
  async getDashboardAnalytics({ commit }, payload) {
    commit('setLoader', { key: 'dashboardAnalytics', value: true });
    try {
      const response = await getDashboardStats('dashboard-stats', payload);
      commit('setDashboardAnalytics', response.data?.dashboard_stats);
    } catch (error) {
      commit('setDashboardAnalytics', []);
    } finally {
      commit('setLoader', { key: 'dashboardAnalytics', value: false });
    }
  },
  async getPerformanceByCampaign({ commit }, payload) {
    const isExport = !!payload.export;
    if (!isExport) {
      commit('setLoader', { key: 'performanceByCampaign', value: true });
    }
    if(isExport) {
      commit('setDownloadLoader', { key: 'performanceByCampaign', value: true });
    }
    try {
      const response = await getDashboardStats('campaign-stats', payload);
      if (isExport && response?.data?.job_data_id) {
        toastMsg(`Performance By campaign download request raised successfully with Job ID (${response?.data?.job_data_id})`, 'success');
        EventBus.emit('reloadDownload');
      } else {
        commit('setPerformanceByCampaign', response.data?.stats_list);
      }
    } catch (error) {
      commit('setPerformanceByCampaign', []);
    } finally {
      commit('setLoader', { key: 'performanceByCampaign', value: false });
      commit('setDownloadLoader', { key: 'performanceByCampaign', value: false });
    }
  },
  async getPerformanceByCreatives({ commit }, payload) {
    let aborted = false
    const isExport = !!payload.export;
    if (!isExport) {
      commit('setLoader', { key: 'performanceByCreatives', value: true });
    }
    if(isExport) {
      commit('setDownloadLoader', { key: 'performanceByCreatives', value: true });
    }
    try {
      const response = await getDashboardStats('creatives-stats', payload);
      if (isExport && response?.data?.job_data_id) {
        toastMsg(`Creatives download request raised successfully with Job ID (${response?.data?.job_data_id})`, 'success');
        EventBus.emit('reloadDownload');
      } else {
        commit('setPerformanceByCreatives', response.data);
      }
    } catch (error) {
      const isAborted = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
      if (isAborted || error?.status === 'cancelled') {
        aborted = true;
        return; // (if you prefer to keep loader spinning)
      } else {
        // genuine error
        commit('setPerformanceByCreatives', []);
      }
    } finally {
      if (!aborted) {
        commit('setLoader', { key: 'performanceByCreatives', value: false });
        commit('setDownloadLoader', { key: 'performanceByCreatives', value: false });
      }
    }
  },
  async getPerformanceByGeo({ commit }, payload) {
    let aborted = false;
    const isExport = !!payload.export;
    if (!isExport) {
      commit('setLoader', { key: 'performanceByGeo', value: true });
    }
    if(isExport) {
      commit('setDownloadLoader', { key: 'performanceByGeo', value: true });
    }
    try {
      const response = await getDashboardStats('geo-stats', payload);
      if (isExport && response?.data?.job_data_id) {
        toastMsg(`Geo download request raised successfully with Job ID (${response?.data?.job_data_id})`, 'success');
        EventBus.emit('reloadDownload');
      } else {
        commit('setPerformanceByGeo', response.data);
      }
    } catch (error) {
      const isAborted = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
      if (isAborted || error?.status === 'cancelled') {
        aborted = true;
        return; // (if you prefer to keep loader spinning)
      } else {
        // genuine error
        commit('setPerformanceByGeo', []);
      }
    } finally {
      if (!aborted) {
        commit('setLoader', { key: 'performanceByGeo', value: false });
        commit('setDownloadLoader', { key: 'performanceByGeo', value: false });
      }
    }
  },
  async getPerformanceByEvents({ commit }, payload) {
    let aborted = false;
    const isExport = !!payload.export;
    if (!isExport) {
      commit('setLoader', { key: 'performanceByEvents', value: true });
    }
    if(isExport) {
      commit('setDownloadLoader', { key: 'performanceByEvents', value: true });
    }
    try {
      const response = await getDashboardStats('campaigns-stats', payload);
      if (isExport && response?.data?.job_data_id) {
        toastMsg(`Campaign download request raised successfully with Job ID (${response?.data?.job_data_id})`, 'success');
        EventBus.emit('reloadDownload');
      } else {
        commit('setPerformanceByEvents', response.data);
      }
    } catch (e) {
      const isAborted = e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
      if (isAborted || e?.status === 'cancelled') {
        aborted = true;
        return; // (if you prefer to keep loader spinning)
      } else {
        // genuine error
        commit('setPerformanceByEvents', []);
      }
    } finally {
      if (!aborted) {
        commit('setLoader', { key: 'performanceByEvents', value: false });
        commit('setDownloadLoader', { key: 'performanceByEvents', value: false });
      }
    }
  },
  async getPerformanceByProviders({ commit }, payload) {
    let aborted = false;
    const isExport = !!payload.export;
    if (!isExport) {
      commit('setLoader', { key: 'performanceByProviders', value: true });
    }
    if(isExport) {
      commit('setDownloadLoader', { key: 'performanceByProviders', value: true });
    }
    try {
      const response = await getDashboardStats('provider-stats', payload);
      if (isExport && response?.data?.job_data_id) {
        toastMsg(`Provider download request raised successfully with Job ID (${response?.data?.job_data_id})`, 'success');
        EventBus.emit('reloadDownload');
      } else {
        commit('setPerformanceByProviders', response.data);
      }
    } catch (error) {
      const isAborted = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
      if (isAborted || error?.status === 'cancelled') {
        aborted = true;
        return; // (if you prefer to keep loader spinning)
      } else {
        // genuine error
        commit('setPerformanceByProviders', []);
      }
    } finally {
      if (!aborted) {
        commit('setLoader', { key: 'performanceByProviders', value: false });
        commit('setDownloadLoader', { key: 'performanceByProviders', value: false });
      }
    }
  },
  async getPerformanceBySports({ commit }, payload) {
    let aborted = false;
    const isExport = !!payload.export;
    if (!isExport) {
      commit('setLoader', { key: 'performanceBySports', value: true });
    }
    if(isExport) {
      commit('setDownloadLoader', { key: 'performanceBySports', value: true });
    }
    try {
      const response = await getDashboardStats(`${payload.type}-stats`, payload);
      if (isExport && response?.data?.job_data_id) {
        toastMsg(`Sports download request raised successfully with Job ID (${response?.data?.job_data_id})`, 'success');
        EventBus.emit('reloadDownload');
      } else {
        commit('setPerformanceBySports', response.data);
      }
    } catch (error) {
      const isAborted = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
      if (isAborted || error?.status === 'cancelled') {
        aborted = true;
        return; // (if you prefer to keep loader spinning)
      } else {
        // genuine error
        commit('setPerformanceBySports', []);
      }
    } finally {
      if (!aborted) {
        commit('setLoader', { key: 'performanceBySports', value: false });
        commit('setDownloadLoader', { key: 'performanceBySports', value: false });
      }
    }
  },
  downloadDashboardsStats({commit}, payload) {
      downloadDashboards(payload).then(res => {
          commit('setDownloadDashboardsSuccess', res.data)
      }, (error) => {
          commit('setDownloadDashboardsErr', error?.response?.data || error)
      })
  },
  markAllReadDownloadNotifications({commit, dispatch}, payload) {
      markAllReadDownload(payload).then(res => {
          dispatch('downloadDashboardsStats', {
              page: 1,
              page_size: 10,
              sort_by: 'id',
              order: 'desc',
              compact: 1
          });
      }, (error) => {
      })
  }
}